import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, MessageCircle, RefreshCw, Trash2, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import logoFeo from "@/assets/feo/logo-feo.png";
import { AdminGate } from "@/components/admin/AdminGate";
import { B2bOverview } from "@/components/admin/B2bOverview";
import { supabase } from "@/integrations/supabase/client";
import { B2B_STAGES, stageMeta, isPlanoGuiado, planoLabel, type B2bStage } from "@/lib/b2b/config";
import {
  listB2bLeads,
  updateB2bLeadStage,
  updateB2bLeadNotes,
  deleteB2bLead,
  listB2bLeadEvents,
  logB2bLeadEvent,
} from "@/lib/b2b/admin.functions";


export const Route = createFileRoute("/admin/feoclinicas")({
  head: () => ({
    meta: [{ title: "FEO Clínicas — CRM" }, { name: "robots", content: "noindex,nofollow" }],
  }),
  component: () => (
    <AdminGate route="/admin/feoclinicas">
      <AdminB2bPage />
    </AdminGate>
  ),
});

type B2bLead = {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  num_fisios: string;
  cargo: string;
  plano: string | null;
  stage: string;
  notes: string | null;
  utm_source: string | null;
  utm_campaign: string | null;
  page_url: string | null;
  created_at: string;
  stage_updated_at: string;
  conversion_number: number;
  is_repeat: boolean;
};

type B2bLeadEvent = {
  id: string;
  event_type: string;
  title: string;
  description: string | null;
  created_at: string;
};

const FC_TEAL = "#3fd5c9";
const FC_BLUE = "#3b82f6";

const EVENT_COLORS: Record<string, string> = {
  conversao: FC_TEAL,
  conversao_repetida: "#f5b022",
  movimentacao: FC_BLUE,
  contato: "#25D366",
  anotacao: "#8b9bb5",
  whatsapp: "#25D366",
  observacao: "#8b9bb5",
};

function waLink(telefone: string, nome: string) {
  const digits = telefone.replace(/\D+/g, "");
  const withCountry = digits.startsWith("55") ? digits : `55${digits}`;
  const primeiro = nome.trim().split(/\s+/)[0] ?? "";
  const msg = `Olá, ${primeiro}! Me chamo Marcus e sou responsável pelo programa FEO para Clínicas. Como você está?`;
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(msg)}`;
}

/** "2h 15min", "3d 4h", "12min", "há poucos segundos" */
function dur(ms: number) {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const restMin = min % 60;
  if (h < 24) return restMin ? `${h}h ${restMin}min` : `${h}h`;
  const d = Math.floor(h / 24);
  const restH = h % 24;
  return restH ? `${d}d ${restH}h` : `${d}d`;
}

function diff(from: string | null | undefined, to: string | null | undefined) {
  if (!from || !to) return null;
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return dur(b - a);
}



/** Registra o clique no WhatsApp — base do "tempo de ativação" do lead. */
function trackWhats(id: string) {
  void logB2bLeadEvent({
    data: { id, event_type: "whatsapp", title: "Clique no WhatsApp (ativação)" },
  }).catch(() => {});
}

async function logoutB2b() {
  await supabase.auth.signOut();
  window.location.href = "/admin";
}

function AdminB2bPage() {
  return <B2bBoard />;
}


function B2bBoard() {
  const [leads, setLeads] = useState<B2bLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<B2bLead | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [tab, setTab] = useState<"visao" | "crm">("visao");

  async function load() {
    setLoading(true);
    try {
      const res = await listB2bLeads();
      setLeads((res?.leads ?? []) as B2bLead[]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar leads.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const byStage = useMemo(() => {
    const map: Record<string, B2bLead[]> = {};
    for (const s of B2B_STAGES) map[s.id] = [];
    for (const l of leads) (map[l.stage] ?? map["sem-contato"]).push(l);
    return map;
  }, [leads]);

  async function moveTo(id: string, stage: B2bStage) {
    const prev = leads;
    setLeads((cur) =>
      cur.map((l) => (l.id === id ? { ...l, stage, stage_updated_at: new Date().toISOString() } : l)),
    );
    try {
      await updateB2bLeadStage({ data: { id, stage } });
    } catch (err) {
      setLeads(prev);
      toast.error(err instanceof Error ? err.message : "Não foi possível mover o lead.");
    }
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-fc-navy text-white">
      <header className="relative shrink-0 border-b border-fc-teal/15 bg-gradient-to-r from-fc-navy via-fc-surface to-fc-navy">
        <span className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-fc-teal/60 via-fc-blue/40 to-transparent" />
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">

          <div className="flex items-center gap-3">
            <img src={logoFeo} alt="Fisio em Ortopedia" className="h-8 w-auto" />
            <div>
              <h1 className="text-sm font-bold">
                FEO para Clínicas <span className="text-fc-teal">· CRM</span>
              </h1>
              <p className="text-xs text-white/55">
                {leads.length} lead{leads.length === 1 ? "" : "s"} no funil
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-white/20 bg-transparent text-white hover:bg-white/10"
              onClick={() => void load()}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Atualizar
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-white/20 bg-transparent text-white hover:bg-white/10"
              onClick={() => void logoutB2b()}
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="shrink-0 border-b border-white/10 px-4">
        <div className="flex gap-1">
          {([
            { id: "visao", label: "Visão geral" },
            { id: "crm", label: "CRM Kanban" },
          ] as const).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === t.id
                  ? "border-fc-teal text-white"
                  : "border-transparent text-white/50 hover:text-white/80"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "visao" ? (
        <div className="min-h-0 flex-1 overflow-auto px-4 py-6">
          <B2bOverview />
        </div>
      ) : loading ? (
        <div className="grid flex-1 place-items-center text-white/60">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto px-4 py-6">
          <div className="flex min-w-max items-start gap-4 pb-2">
            {B2B_STAGES.map((s) => (
              <div
                key={s.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragging) void moveTo(dragging, s.id);
                  setDragging(null);
                }}
                className="w-[280px] shrink-0 rounded-2xl border border-white/10 bg-white/[0.03] p-3"
              >

                <div className="mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                    <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                    {s.label}
                  </span>
                  <span className="text-xs text-white/45">{byStage[s.id]?.length ?? 0}</span>
                </div>

                <div className="space-y-2.5">
                  {(byStage[s.id] ?? []).map((l) => (
                    <article
                      key={l.id}
                      draggable
                      onDragStart={() => setDragging(l.id)}
                      onDragEnd={() => setDragging(null)}
                      onClick={() => setOpen(l)}
                      className="cursor-pointer rounded-xl border border-white/10 bg-fc-surface p-3 transition-colors hover:border-white/25"
                    >
                      <h3 className="truncate text-sm font-semibold">{l.nome}</h3>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                          style={{
                            background: `${isPlanoGuiado(l.plano) ? FC_BLUE : FC_TEAL}22`,
                            color: isPlanoGuiado(l.plano) ? "#93c5fd" : FC_TEAL,
                          }}
                        >
                          B2B · {l.plano ? planoLabel(l.plano) : "sem plano"}
                        </span>
                        <span className="rounded-full bg-white/8 px-2 py-0.5 text-[10px] text-white/65">
                          {l.num_fisios}
                        </span>
                        {l.is_repeat && (
                          <span className="rounded-full bg-[#f5b022]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-admin-accent">
                            {l.conversion_number}ª conversão
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-[10px] text-white/40">
                          {new Date(l.created_at).toLocaleDateString("pt-BR")}
                        </span>
                        <a
                          href={waLink(l.telefone, l.nome)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            trackWhats(l.id);
                          }}
                          className="inline-flex items-center gap-1 rounded-lg bg-[#25D366]/15 px-2 py-1 text-[11px] font-semibold text-[#25D366] hover:bg-[#25D366]/25"
                        >
                          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                        </a>
                      </div>
                    </article>
                  ))}
                  {(byStage[s.id]?.length ?? 0) === 0 && (
                    <p className="rounded-xl border border-dashed border-white/10 p-4 text-center text-[11px] text-white/35">
                      arraste um lead para cá
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <LeadDialog
        lead={open}
        onClose={() => setOpen(null)}
        onMove={(stage) => {
          if (open) void moveTo(open.id, stage);
          setOpen((cur) => (cur ? { ...cur, stage } : cur));
        }}
        onNotesSaved={(notes) => {
          setLeads((cur) => cur.map((l) => (open && l.id === open.id ? { ...l, notes } : l)));
        }}
        onDeleted={(id) => {
          setLeads((cur) => cur.filter((l) => l.id !== id));
          setOpen(null);
        }}
      />

    </div>
  );
}

function LeadDialog({
  lead,
  onClose,
  onMove,
  onNotesSaved,
  onDeleted,
}: {
  lead: B2bLead | null;
  onClose: () => void;
  onMove: (stage: B2bStage) => void;
  onNotesSaved: (notes: string) => void;
  onDeleted: (id: string) => void;
}) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [events, setEvents] = useState<B2bLeadEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [newEvent, setNewEvent] = useState("");

  async function loadEvents(id: string) {
    setLoadingEvents(true);
    try {
      const res = await listB2bLeadEvents({ data: { id } });
      setEvents((res?.events ?? []) as B2bLeadEvent[]);
    } catch {
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }

  async function addEvent() {
    if (!lead || !newEvent.trim()) return;
    try {
      await logB2bLeadEvent({
        data: { id: lead.id, event_type: "contato", title: newEvent.trim() },
      });
      setNewEvent("");
      await loadEvents(lead.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao registrar evento.");
    }
  }

  async function remove() {
    if (!lead) return;
    if (!window.confirm(`Excluir definitivamente o lead "${lead.nome}"?`)) return;
    setDeleting(true);
    try {
      await deleteB2bLead({ data: { id: lead.id } });
      onDeleted(lead.id);
      toast.success("Lead excluído.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao excluir o lead.");
    } finally {
      setDeleting(false);
    }
  }


  useEffect(() => {
    setNotes(lead?.notes ?? "");
    setNewEvent("");
    if (lead?.id) void loadEvents(lead.id);
    else setEvents([]);
  }, [lead?.id, lead?.notes]);

  async function save() {
    if (!lead) return;
    setSaving(true);
    try {
      await updateB2bLeadNotes({ data: { id: lead.id, notes } });
      onNotesSaved(notes);
      await loadEvents(lead.id);
      toast.success("Anotações salvas.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar anotações.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={lead !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {lead && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" /> {lead.nome}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              <section className="space-y-1.5 text-sm">
                <p>
                  <span className="text-muted-foreground">E-mail:</span> {lead.email}
                </p>
                <p>
                  <span className="text-muted-foreground">WhatsApp:</span> {lead.telefone}
                </p>
                <p className="text-xs text-muted-foreground">
                  Entrou em {new Date(lead.created_at).toLocaleString("pt-BR")}
                  {lead.utm_source ? ` · origem: ${lead.utm_source}` : ""}
                  {lead.utm_campaign ? ` · campanha: ${lead.utm_campaign}` : ""}
                </p>
                <a
                  href={waLink(lead.telefone, lead.nome)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    trackWhats(lead.id);
                    void loadEvents(lead.id);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1fb757]"
                >
                  <MessageCircle className="h-4 w-4" /> Abrir WhatsApp
                </a>
              </section>

              <section className="rounded-xl border border-border bg-muted/30 p-4 text-sm">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Qualificação
                </h3>
                <dl className="space-y-2.5">
                  <div>
                    <dt className="text-xs text-muted-foreground">
                      Quantos fisioterapeutas tem na clínica?
                    </dt>
                    <dd className="font-medium">{lead.num_fisios}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Qual cargo ocupa na clínica?</dt>
                    <dd className="font-medium">{lead.cargo}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Plano de interesse</dt>
                    <dd className="font-medium">{lead.plano ?? "não informado"}</dd>
                  </div>
                </dl>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Etapa do funil
                </h3>
                <div className="flex flex-wrap gap-2">
                  {B2B_STAGES.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onMove(s.id)}
                      className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium ${
                        lead.stage === s.id
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border hover:bg-muted"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Atual: {stageMeta(lead.stage).label} · atualizado em{" "}
                  {new Date(lead.stage_updated_at).toLocaleString("pt-BR")}
                </p>
              </section>

              <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Anotações
                </h3>
                <Textarea
                  rows={6}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Registro das conversas, reuniões agendadas, número de fisios, valores discutidos..."
                />
                <Button className="mt-3 w-full" onClick={() => void save()} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar anotações"}
                </Button>
              </section>

              <section className="border-t border-border pt-4">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Tempos do lead
                </h3>
                {(() => {
                  const firstWhats = events.find((e) => e.event_type === "whatsapp");
                  const firstMove = events.find((e) => e.event_type === "movimentacao");
                  const lastMove = [...events].reverse().find((e) => e.event_type === "movimentacao");
                  const cards: { label: string; value: string | null }[] = [
                    {
                      label: "Entrada → 1º WhatsApp",
                      value: diff(lead.created_at, firstWhats?.created_at),
                    },
                    {
                      label: "Entrada → 1ª movimentação",
                      value: diff(lead.created_at, firstMove?.created_at),
                    },
                    {
                      label: "Parado na etapa atual",
                      value: diff(lastMove?.created_at ?? lead.stage_updated_at, new Date().toISOString()),
                    },
                    {
                      label: "Tempo total no funil",
                      value: diff(lead.created_at, new Date().toISOString()),
                    },
                  ];
                  return (
                    <div className="grid grid-cols-2 gap-2">
                      {cards.map((c) => (
                        <div key={c.label} className="rounded-lg border border-border bg-muted/30 p-2.5">
                          <div className="text-[11px] text-muted-foreground">{c.label}</div>
                          <div className="text-sm font-bold">{c.value ?? "—"}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </section>

              <section className="border-t border-border pt-4">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Histórico completo
                </h3>
                <div className="mb-3 flex gap-2">
                  <Input
                    placeholder="Registrar contato/observação..."
                    value={newEvent}
                    onChange={(e) => setNewEvent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void addEvent();
                      }
                    }}
                  />
                  <Button variant="outline" onClick={() => void addEvent()} disabled={!newEvent.trim()}>
                    Add
                  </Button>
                </div>
                {loadingEvents ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : events.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sem registros ainda.</p>
                ) : (
                  <ol className="space-y-3 border-l border-border pl-4">
                    <li className="relative">
                      <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-muted-foreground/50" />
                      <p className="text-sm font-medium">Lead entrou</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                        {new Date(lead.created_at).toLocaleString("pt-BR")}
                      </p>
                    </li>
                    {events.map((ev, i) => {
                      const prev = i === 0 ? lead.created_at : events[i - 1]!.created_at;
                      const gap = diff(prev, ev.created_at);
                      const total = diff(lead.created_at, ev.created_at);
                      return (
                        <li key={ev.id} className="relative">
                          <span
                            className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full"
                            style={{ background: EVENT_COLORS[ev.event_type] ?? "#8b9bb5" }}
                          />
                          <p className="text-sm font-medium">{ev.title}</p>
                          {ev.description && (
                            <p className="text-xs text-muted-foreground">{ev.description}</p>
                          )}
                          <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                            {new Date(ev.created_at).toLocaleString("pt-BR")}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {gap && (
                              <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                +{gap} desde o anterior
                              </span>
                            )}
                            {total && (
                              <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                                {total} desde a entrada
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>

              <section className="border-t border-border pt-4">
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => void remove()}
                  disabled={deleting}
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="mr-1.5 h-4 w-4" /> Excluir lead
                    </>
                  )}
                </Button>
              </section>

            </div>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
}
